import { isNativeApp } from "./runtime";

const NATIVE_PICKER_CANCELLED_CODES = new Set([
  "OS-PLUG-CAMR-0006", // takePhoto cancelled
  "OS-PLUG-CAMR-0020", // chooseFromGallery cancelled
]);

const normalizeImageFormat = (value) => {
  const format = String(value || "jpeg").trim().toLowerCase();
  if (format === "jpg" || format === "jpeg") {
    return { extension: "jpg", mime: "image/jpeg" };
  }
  if (format === "png") return { extension: "png", mime: "image/png" };
  if (format === "gif") return { extension: "gif", mime: "image/gif" };
  if (format === "webp") return { extension: "webp", mime: "image/webp" };
  if (format === "heic" || format === "heif") {
    return { extension: format, mime: `image/${format}` };
  }
  return { extension: "jpg", mime: "image/jpeg" };
};

const mediaResultToFile = async (result, index) => {
  const source = String(result?.webPath || "").trim();
  if (!source) throw new Error("Native photo picker returned no readable image path.");

  const response = await fetch(source);
  if (!response.ok) {
    throw new Error(`Could not read selected image (${response.status}).`);
  }

  const blob = await response.blob();
  const normalized = normalizeImageFormat(
    result?.metadata?.format || String(blob?.type || "").replace(/^image\//, "")
  );
  const mime = String(blob?.type || "").startsWith("image/")
    ? blob.type
    : normalized.mime;
  const name = `chat-photo-${Date.now()}-${index + 1}.${normalized.extension}`;

  return new File([blob], name, {
    type: mime,
    lastModified: Date.now(),
  });
};

export async function pickNativeChatPhotos({ limit = 5, source = "library" } = {}) {
  if (!isNativeApp()) return null;

  const normalizedLimit = Math.max(1, Number(limit || 1));
  const normalizedSource = String(source || "library").trim().toLowerCase();

  // Remove textarea focus before presenting the native controller. The native
  // picker is fullscreen, so it does not inherit WebKit's stale popover anchor
  // from the keyboard-shifted composer.
  try {
    const activeElement = document.activeElement;
    if (activeElement?.matches?.("textarea, input, [contenteditable='true']")) {
      activeElement.blur();
    }
  } catch {
    // ignore focus cleanup failures
  }

  try {
    const { Keyboard } = await import("@capacitor/keyboard");
    await Keyboard.hide().catch(() => {});
  } catch {
    // The fullscreen native picker still works if keyboard cleanup is unavailable.
  }

  try {
    const { Camera, CameraDirection, EncodingType, MediaTypeSelection } = await import("@capacitor/camera");
    if (normalizedSource === "camera") {
      const result = await Camera.takePhoto({
        quality: 100,
        correctOrientation: true,
        encodingType: EncodingType.JPEG,
        saveToGallery: false,
        cameraDirection: CameraDirection.Rear,
        includeMetadata: true,
        editable: "no",
        presentationStyle: "fullscreen",
      });
      return [await mediaResultToFile(result, 0)];
    }

    const selection = await Camera.chooseFromGallery({
      mediaType: MediaTypeSelection.Photo,
      allowMultipleSelection: normalizedLimit > 1,
      limit: normalizedLimit,
      includeMetadata: true,
      editable: "no",
      presentationStyle: "fullscreen",
      quality: 100,
      correctOrientation: true,
    });
    const results = Array.isArray(selection?.results)
      ? selection.results.slice(0, normalizedLimit)
      : [];

    return await Promise.all(results.map(mediaResultToFile));
  } catch (error) {
    const code = String(error?.code || "");
    const message = String(error?.message || "");
    if (NATIVE_PICKER_CANCELLED_CODES.has(code) || /cancel(?:led|ed)/i.test(message)) {
      return [];
    }
    throw error;
  }
}
